import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSubscription } from '../../hooks/useSubscription';
import { useAuth } from '../../contexts/AuthContext';
import { Spinner } from '../common/Spinner';

interface SubscriptionGuardProps {
  children: React.ReactNode;
  fallbackUrl?: string;
}

export function SubscriptionGuard({
  children,
  fallbackUrl = '/pricing'
}: SubscriptionGuardProps) {
  const { isActive, loading, error } = useSubscription();
  const { user } = useAuth();
  const location = useLocation();

  // Not logged in
  if (!user) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Show loading state during initialization or data fetch
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner variant="border" size="lg" className="text-blue-600" />
      </div>
    );
  }

  // Error fetching subscription status
  if (error) {
    console.error('Subscription check failed:', error);
    return (
      <Navigate
        to={fallbackUrl}
        state={{
          from: location.pathname,
          message: 'Unable to verify subscription status. Please try again or contact support.'
        }}
        replace
      />
    );
  }

  // Check subscription status
  if (!isActive) {
    return (
      <Navigate
        to={fallbackUrl}
        state={{
          from: location.pathname,
          message: 'This feature requires an active subscription.'
        }}
        replace
      />
    );
  }

  // Render children if user has access
  return <>{children}</>;
}
